import { useEffect, useState } from "react";
import { api, type Anchor, type AuditEntry, type VerifyResponse } from "../api.ts";

function subjectOf(e: AuditEntry): string {
  const v = (e as Record<string, unknown>).subjectRef;
  return typeof v === "string" ? v : "";
}

export function Audit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [anchors, setAnchors] = useState<Anchor[]>([]);
  const [verify, setVerify] = useState<VerifyResponse | null>(null);
  const [verifyErr, setVerifyErr] = useState("");
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<number | null>(null);

  async function load() {
    setErr("");
    try {
      setEntries((await api.listAudit()).entries ?? []);
      setAnchors((await api.listAnchors()).anchors ?? []);
    } catch (e) {
      setErr((e as Error).message);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function runVerify() {
    setVerifyErr("");
    setVerify(null);
    try {
      setVerify(await api.verifyAudit());
    } catch (e) {
      setVerifyErr((e as Error).message);
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Audit</h2>
        <button className="ghost" onClick={() => void load()}>
          Refresh
        </button>
      </div>
      {err && <div className="err">{err}</div>}

      <div className="card">
        <h3>Integrity</h3>
        <p className="muted small">
          The audit log is hash-chained and anchored on-chain. Verification fails if any entry was altered, removed, or the
          tail was truncated.
        </p>
        <button onClick={() => void runVerify()}>Verify chain</button>
        {verifyErr && <div className="err">tampering / verification failed: {verifyErr}</div>}
        {verify && (
          <p style={{ marginTop: 12 }}>
            <span className="badge ok">verified</span>{" "}
            <span className="mono small">
              {verify.count} entries · head <code>{verify.headHash.slice(0, 24)}…</code>
            </span>
          </p>
        )}
      </div>

      <div className="card">
        <h3>On-chain anchors ({anchors.length})</h3>
        {anchors.length === 0 ? (
          <p className="muted">No anchors yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>seq</th>
                <th>head</th>
                <th>chain</th>
                <th>tx</th>
              </tr>
            </thead>
            <tbody>
              {anchors.map((a) => (
                <tr key={a.seq}>
                  <td>{a.seq}</td>
                  <td className="mono">{a.headHash.slice(0, 18)}…</td>
                  <td>{a.chainId ?? "-"}</td>
                  <td className="mono">{a.txHash ? `${a.txHash.slice(0, 14)}…` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h3>Events ({entries.length})</h3>
        <label style={{ margin: "0 0 10px" }}>
          Filter by type or subject <span className="muted">(e.g. “policy”, “intent”, an id…)</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="policy" />
        </label>
        {entries.length === 0 ? (
          <p className="muted">No audit events.</p>
        ) : (
          (() => {
            const shown = entries
              .slice()
              .reverse()
              .filter((e) => {
                const t = q.trim().toLowerCase();
                if (!t) return true;
                return e.type.toLowerCase().includes(t) || subjectOf(e).toLowerCase().includes(t) || (e.org ?? "").toLowerCase().includes(t);
              });
            return (
              <>
                <p className="muted small">Showing {shown.length} — click a row for full details.</p>
                <table>
                  <thead>
                    <tr>
                      <th>seq</th>
                      <th>type</th>
                      <th>subject</th>
                      <th>org</th>
                      <th>time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((e) => (
                      <tr key={e.seq} className="click" onClick={() => setOpen(open === e.seq ? null : e.seq)}>
                        <td>{e.seq}</td>
                        <td>{e.type}</td>
                        <td className="mono small">{subjectOf(e) || "-"}</td>
                        <td>{e.org ?? "-"}</td>
                        <td className="small">{e.at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {open !== null && shown.find((e) => e.seq === open) && (
                  <details open style={{ marginTop: 10 }}>
                    <summary className="muted small">Event #{open} details</summary>
                    <pre>{JSON.stringify(shown.find((e) => e.seq === open), null, 2)}</pre>
                  </details>
                )}
              </>
            );
          })()
        )}
      </div>
    </>
  );
}
